import KeyboardArrowRightOutlined from '@mui/icons-material/KeyboardArrowRightOutlined';
import { Avatar, Box, Chip, Divider, Typography, useTheme } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useState } from 'react';
import { User_Role_Enum, UserRoleEnum } from '@kheops/graphql';
import useOpenable from '../../hooks/useOpenable';
import PhotosUtils from '../../common/utils/photos.utils';
import { useInsertRoleAssignmentMutation } from '../../mutations/__generated__/insertRoleAssignment.generated';
import { GetCompanyUsersDocument } from '../../queries/__generated__/getCompanyUsers.generated';
import { currentContextAtom } from '../../state';
import { useDeleteRoleAssignmentMutation } from '../../mutations/__generated__/deleteRoleAssignment.generated';
import { ellipsisStyle } from '../../common/utils/style.utils';
import { getStateLayerColor, StateLayer } from '../../theme/color.utils';
import CompanyTeamUserDetail from './CompanyTeamUserDetail';
import { useCompanyTeamRoleWording } from './useCompanyTeamRoleWording';
import { UserRowData } from './CompanyTeamPage';

interface CompanyTeamUserRowProps {
  data: UserRowData;
}

export default function CompanyTeamUserRow({ data }: CompanyTeamUserRowProps): React.JSX.Element {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'company', 'settings']);
  const { companyId, companyBrand } = useAtomValue(currentContextAtom);
  const { open, isOpen, close } = useOpenable();
  const [insertRole] = useInsertRoleAssignmentMutation({
    refetchQueries: [GetCompanyUsersDocument],
  });
  const [deleteRole] = useDeleteRoleAssignmentMutation({
    refetchQueries: [GetCompanyUsersDocument],
  });
  const [loading, setLoading] = useState(false);
  const resolvedWording = useCompanyTeamRoleWording(companyBrand || undefined);

  const updateRole = useCallback(async (newRole?: User_Role_Enum, newAdminStatus?: boolean) => {
    setLoading(true);

    if (newAdminStatus !== undefined && data.isAdmin !== newAdminStatus) {
      if (newAdminStatus) {
        await insertRole({
          variables: {
            companyId,
            role: UserRoleEnum.CompanyAdmin,
            userId: data.user.id,
          },
        });
      } else {
        await deleteRole({
          variables: {
            companyId,
            role: UserRoleEnum.CompanyAdmin,
            userId: data.user.id,
          },
        });
      }
    }

    if (newRole && data.role !== newRole) {
      await insertRole({
        variables: {
          companyId,
          role: newRole as unknown as UserRoleEnum,
          userId: data.user.id,
        },
      });

      await deleteRole({
        variables: {
          companyId,
          role: data.role as unknown as UserRoleEnum,
          userId: data.user.id,
        },
      });
    }

    setLoading(false);
    close();
  }, [data.isAdmin, data.role]);

  return (
    <>
      <CompanyTeamUserDetail
        open={isOpen}
        close={updateRole}
        loading={loading}
        data={data}
      />
      <Box
        onClick={open}
        sx={{
          display: 'flex',
          gap: 1.5,
          transition: 'all 0.25s ease-out',
          p: 1.5,
          cursor: 'pointer',
          borderRadius: 2,
          '&:hover': {
            backgroundColor: getStateLayerColor(StateLayer.Layer8, theme.palette.surface.main, theme.palette.onSurface.main),
          },
          '&:active': {
            backgroundColor: getStateLayerColor(StateLayer.Layer12, theme.palette.surface.main, theme.palette.onSurface.main),
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              md: 'row',
            },
            gap: {xs: 3, md: 1.5},
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
            <Avatar
              size={80}
              src={PhotosUtils.getUserAvatarPicture(data.user)}
              variant="square"
              sx={{
                borderRadius: 6,
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Typography variant="bodyLarge" sx={{ ...ellipsisStyle }}>
                {data.user.first_name} {data.user.last_name}
              </Typography>
              <Typography variant="bodyMedium" color="onSurfaceVariant" sx={{ ...ellipsisStyle }}>
                {data.user.email}
              </Typography>
              {data.isAdmin && (
                <Chip
                  variant="tag"
                  color="tertiaryContainer"
                  label={t('company:admin')}
                  sx={{
                    mt: 1,
                    width: 'fit-content',
                  }}
                />
              )}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              flex: 1,
              gap: 0.5,
            }}
          >
            <Typography variant="bodySmall">
              {t('company:role')}
            </Typography>
            <Chip variant="tag" label={t(resolvedWording[data.role])} />
          </Box>
        </Box>
        <Box>
          <KeyboardArrowRightOutlined />
        </Box>
      </Box>
      <Divider
            flexItem            
            sx={{
              my: {xs: 2, md: 3},
            }}
          />
    </>
  );
}
